import { IconFileText, IconWorld } from "@tabler/icons-react";
import { Link, useNavigate } from "@tanstack/react-router";
import { CATEGORIAS_DOCUMENTOS, CATEGORIAS_DOCUMENTOS_INVOICE } from "@/features/documentos/constants";
import { useDocumentosPendientes } from "@/features/documentos/hooks/use-documentos-pendientes";
import { useEnviarAAutorizacion, useReenviarSolicitud } from "@/features/solicitudes/hooks/use-envio-autorizacion";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { useSolicitudPolling } from "@/features/solicitudes/hooks/use-solicitud-polling";
import { Badge, Card, Group, Loader, PageHeader, Stack, Text, ThemeIcon } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { useAuthStore } from "@/lib/auth-store";
import type { CategoriaDocumento } from "@/lib/types";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/documentos";
import { CategoriaCard } from "./CategoriaCard";
import { ChecklistResumen } from "./ChecklistResumen";
import { DocumentosActions } from "./DocumentosActions";
import { EstadoBanners } from "./EstadoBanners";

const NUMERO_CFDI = 1;

/**
 * Container del paso "Documentos" · orquesta queries, deriva flags de
 * permisos y compone los subcomponentes. La complejidad de estado
 * (pendientes Map, polling condicional) vive en hooks dedicados.
 */
export function DocumentosPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: sol } = useSolicitud(id);
  const { pendientes, agregar, porCategoria } = useDocumentosPendientes(id);
  const esContrapago = sol.subtipo === "CONTRAPAGO";
  // Invoice (factura extranjera): el PDF del Invoice ya se cargó (paso OCR) · el checklist
  // documental es certificado de residencia fiscal (1) + Documentación Adicional.
  const esInvoice = sol.subtipo === "INVOICE";
  const categorias = esInvoice ? CATEGORIAS_DOCUMENTOS_INVOICE : CATEGORIAS_DOCUMENTOS;

  // Polling agresivo cuando hay archivos en vuelo (1.5s) · suave si la
  // solicitud está en flujo activo (5s) · apagado en cualquier otro caso.
  const enFlujoEditable = sol.estado === "ProvisionalmenteListo" || sol.estado === "AdjuntandoDocumentos";
  const pollMs = pendientes.size > 0 ? 1_500 : enFlujoEditable ? 5_000 : false;
  useSolicitudPolling(id, pollMs);

  const user = useAuthStore.getState().user;
  const esDueno = user?.id === sol.solicitanteId;
  const esSolicitanteDueno = (user?.roles.includes("Solicitante") ?? false) && esDueno;

  const puedeAdjuntar = esSolicitanteDueno && (sol.estado === "ProvisionalmenteListo" || sol.estado === "AdjuntandoDocumentos" || sol.estado === "RechazadaParaCorreccion");
  const enviadaAAutorizacion = sol.estado === "ListoParaAutorizacion" || sol.estado === "EnAutorizacionNivel1" || sol.estado === "EnAutorizacionNivelN";
  const fueRechazada = esSolicitanteDueno && sol.estado === "RechazadaParaCorreccion";
  const nivelDelRechazo = fueRechazada ? sol.flujo?.nivelActual ?? null : null;
  const esSoloLectura = !esSolicitanteDueno;

  const enviar = useEnviarAAutorizacion(id, { onSuccess: () => navigate({ to: "/solicitudes/$id", params: { id }, replace: true }) });
  const reenviar = useReenviarSolicitud(id, { onSuccess: () => navigate({ to: "/solicitudes/$id", params: { id }, replace: true }) });

  const documentosPorCategoria = (cat: CategoriaDocumento) => sol.checklist.documentos.filter((d) => d.categoria === cat);

  // Un pendiente re-inyectado ya se pinta como fila normal del checklist ·
  // no mostrarlo ADEMÁS como fila "esperando" (el badge del header y el
  // polling sí usan el total crudo · siguen activos hasta la confirmación).
  const enChecklist = new Set(sol.checklist.documentos.map((d) => d.documentoId));
  const pendientesVisibles = (cat: CategoriaDocumento) => porCategoria(cat).filter((e) => !enChecklist.has(e.id));

  const countPendientes = (cat: CategoriaDocumento) => pendientesVisibles(cat).length;

  /**
   * Navega al step anterior del wizard · "Regresar a Prorrateo".
   * Cubre ambos casos:
   *   - Solo lectura: el usuario revisa el prorrateo previo.
   *   - Editable: el usuario puede corregir si se equivocó antes de enviar.
   * URL depende del subtipo (multi-CFDI → /prorrateo-multiple, CFDI único → /prorrateo).
   */
  const onVolverAProrrateo = () =>
    navigate({
      to:
        esContrapago || esInvoice
          ? "/solicitudes/$id/prorrateo-sin-cfdi"
          : sol.cfdis.length > 1
            ? "/solicitudes/$id/prorrateo-multiple"
            : "/solicitudes/$id/prorrateo",
      params: { id },
    });

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title={`Documentos · Solicitud #${sol.numeroSolicitud}`}
        description={`${sol.cfdi ? `CFDI ${sol.cfdi.uuid.slice(0, 8)}…` : "Sin CFDI"} · Estado ${sol.estado}`}
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: `#${sol.numeroSolicitud}`, href: `/solicitudes/${id}` },
          { label: "Documentos" },
        ]}
        actions={
          pendientes.size > 0 ? (
            <Badge variant="light" color="info">
              <Group gap={4} wrap="nowrap">
                <Loader size="xs" color="multiva" />
                esperando dispatcher · {pendientes.size} archivo{pendientes.size === 1 ? "" : "s"}
              </Group>
            </Badge>
          ) : null
        }
      />
      <LayoutConStepper estado={sol.estado} subtipo={sol.subtipo} tipo={sol.tipo} paso="documentos" solicitudId={id} contabilizada={sol.enviadaAContabilizacion} fillHeight>
        <Stack gap="md">
          <ChecklistResumen
            checklist={sol.checklist}
            pendientesXml={countPendientes("FacturaXml")}
            pendientesPdf={countPendientes("FacturaPdf")}
            pendientesAdicional={countPendientes("Adicional")}
            pendientesCertificado={countPendientes("CertificadoResidencia")}
            solicitudId={id}
            esContrapago={esContrapago}
            esInvoice={esInvoice}
          />

          <EstadoBanners
            enviadaAAutorizacion={enviadaAAutorizacion}
            fueRechazada={fueRechazada}
            esSoloLectura={esSoloLectura}
            puedeAdjuntar={puedeAdjuntar}
            nivelDelRechazo={nivelDelRechazo}
            motivosRechazo={sol.ultimoMotivoRechazo}
          />

          {/* Invoice · el PDF del Invoice ya se cargó en el paso Factura (PDF) · card
              informativa (auto-satisfecha) con acceso a ese paso para revisarlo/reemplazarlo. */}
          {esInvoice && (
            <Card padding="lg" style={{ borderLeft: "3px solid var(--color-multiva-400)" }}>
              <Group justify="space-between" align="center" wrap="nowrap">
                <Group gap="sm" align="center" wrap="nowrap">
                  <ThemeIcon size="md" variant="filled" color="multiva">
                    <IconWorld size={18} stroke={1.6} />
                  </ThemeIcon>
                  <div>
                    <Group gap={8} align="center">
                      <Text fw={600} size="sm">
                        PDF del Invoice
                      </Text>
                      <Badge variant="light" color="multiva">
                        Cargado
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed" className="mt-0.5">
                      La factura extranjera (PDF) ya se cargó y extrajo por OCR en el paso Factura (PDF).
                    </Text>
                  </div>
                </Group>
                <Link to="/solicitudes/$id/carga-invoice" params={{ id }} className="text-sm font-medium text-multiva-600 hover:underline whitespace-nowrap">
                  <Group gap={4} wrap="nowrap">
                    <IconFileText size={14} /> Ver / reemplazar
                  </Group>
                </Link>
              </Group>
            </Card>
          )}

          {/* Factura XML/PDF (FB60) provienen del paso CFDI · sus archivos + acciones
              viven en el checklist de arriba. Aquí solo se cargan las categorías
              restantes (Adicional; Invoice: certificado de residencia + Adicional). */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {categorias.filter((cat) => cat.key !== "FacturaXml" && cat.key !== "FacturaPdf").map((cat) => (
              <CategoriaCard
                key={cat.key}
                cat={cat}
                documentos={documentosPorCategoria(cat.key)}
                esperando={pendientesVisibles(cat.key)}
                solicitudId={id}
                numeroCfdi={NUMERO_CFDI}
                puedeAdjuntar={puedeAdjuntar}
                onSubidoPendiente={agregar}
              />
            ))}
          </div>

          <DocumentosActions
            checklistCompleto={sol.checklist.completoFB60}
            enviadaAAutorizacion={enviadaAAutorizacion}
            puedeAdjuntar={puedeAdjuntar}
            fueRechazada={fueRechazada}
            nivelDelRechazo={nivelDelRechazo}
            enviando={enviar.isPending}
            reenviando={reenviar.isPending}
            onVolver={onVolverAProrrateo}
            onEnviar={() => enviar.mutate()}
            onReenviar={() => reenviar.mutate()}
          />
        </Stack>
      </LayoutConStepper>
    </div>
  );
}
